import type { ChangeEvent } from "react";
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

import { toFormikValidate } from "@thalamos/common";
import type { CreatePersonRequest } from "@thalamos/haven-common";

import {
  CreatePersonValuesSchema,
  type CreatePersonValues,
} from "../../pages/Persons/createPerson.js";
import { AddressPicker } from "../AddressPicker/AddressPicker.js";
import type { PersonSaveOutcome } from "../PersonForm/PersonForm.js";

const KnownQuestion = ({
  id,
  name,
  label,
  value,
  error,
  onChange,
}: {
  id: string;
  name: keyof CreatePersonValues;
  label: string;
  value: string;
  error: string | undefined;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation();

  return (
    <FormControl error={error !== undefined}>
      <FormLabel id={id}>{label}</FormLabel>
      <RadioGroup
        aria-labelledby={id}
        name={name}
        value={value}
        onChange={onChange}
        row
      >
        <FormControlLabel
          value="yes"
          control={<Radio />}
          label={t("personPage.creation.yes")}
        />
        <FormControlLabel
          value="no"
          control={<Radio />}
          label={t("personPage.creation.no")}
        />
      </RadioGroup>
      {error === undefined ? null : <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

/**
 * Registers a young person with what is known about them when they are first
 * met: their name, and their date of birth and where they live where those are
 * known. Anything else is recorded on the record afterwards. Validated by
 * `CreatePersonValuesSchema`.
 */
export const CreatePersonForm = ({
  initialValues,
  onSave,
  placesApiKey,
}: {
  initialValues: CreatePersonValues;
  onSave: (request: CreatePersonRequest) => Promise<PersonSaveOutcome>;
  placesApiKey: string;
}) => {
  const { t } = useTranslation();

  return (
    <Formik<CreatePersonValues>
      initialValues={initialValues}
      validate={toFormikValidate(CreatePersonValuesSchema)}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, helpers) => {
        // A rejection is caught here rather than left to Formik, which would
        // report it to the console and leave a form that did nothing and said
        // nothing.
        const outcome = await onSave(
          CreatePersonValuesSchema.parse(values),
        ).catch((): PersonSaveOutcome => "failed");

        helpers.setStatus(outcome === "saved" ? undefined : outcome);
      }}
    >
      {({ values, errors, status, isSubmitting, handleChange, setValues }) => {
        const errorFor = (field: keyof CreatePersonValues) =>
          errors[field] === undefined ? undefined : t(errors[field]);

        return (
          <Form noValidate>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      id="register-given"
                      name="given"
                      label={t("personPage.creation.given.label")}
                      value={values.given}
                      onChange={handleChange}
                      error={errorFor("given") !== undefined}
                      helperText={errorFor("given")}
                      fullWidth
                    />
                    <TextField
                      id="register-family"
                      name="family"
                      label={t("personPage.creation.family.label")}
                      value={values.family}
                      onChange={handleChange}
                      error={errorFor("family") !== undefined}
                      helperText={errorFor("family")}
                      fullWidth
                    />
                  </Stack>

                  <KnownQuestion
                    id="register-date-of-birth-known"
                    name="dateOfBirthKnown"
                    label={t("personPage.creation.dateOfBirthKnown.label")}
                    value={values.dateOfBirthKnown}
                    error={errorFor("dateOfBirthKnown")}
                    onChange={handleChange}
                  />

                  {values.dateOfBirthKnown === "yes" ? (
                    <TextField
                      id="register-date-of-birth"
                      name="dateOfBirth"
                      type="date"
                      label={t("personPage.dateOfBirth.label")}
                      value={values.dateOfBirth}
                      onChange={handleChange}
                      error={errorFor("dateOfBirth") !== undefined}
                      helperText={errorFor("dateOfBirth")}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ maxWidth: 280 }}
                    />
                  ) : null}

                  <KnownQuestion
                    id="register-home-address-known"
                    name="homeAddressKnown"
                    label={t("personPage.creation.homeAddressKnown.label")}
                    value={values.homeAddressKnown}
                    error={errorFor("homeAddressKnown")}
                    onChange={handleChange}
                  />

                  {values.homeAddressKnown === "yes" ? (
                    <AddressPicker
                      idPrefix="register"
                      value={{
                        address: values.address,
                        postcode: values.postcode,
                      }}
                      errors={{
                        address: errorFor("address"),
                        postcode: errorFor("postcode"),
                      }}
                      onChange={(picked) => {
                        void setValues({ ...values, ...picked });
                      }}
                      placesApiKey={placesApiKey}
                    />
                  ) : null}

                  <TextField
                    id="register-upn"
                    name="upn"
                    label={t("personPage.creation.upn")}
                    value={values.upn}
                    onChange={handleChange}
                    error={errorFor("upn") !== undefined}
                    helperText={errorFor("upn")}
                    sx={{ maxWidth: 320 }}
                  />
                </Stack>
              </Paper>

              {status === "failed" ? (
                <Alert severity="error">{t("personPage.saveFailed")}</Alert>
              ) : null}

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{ alignSelf: "flex-start" }}
              >
                {t("personPage.creation.submit")}
              </Button>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
